// Pure formatting helpers used across views. Kept dependency-free so they
// can be imported anywhere without pulling in React.

export type RangePreset = "24h" | "7d" | "30d" | "custom";

export function presetRange(
  p: RangePreset,
  customStart: string,
  customEnd: string,
): { start: Date; end: Date } | null {
  const now = new Date();
  if (p === "custom") {
    if (!customStart || !customEnd) return null;
    return { start: new Date(customStart), end: new Date(customEnd) };
  }
  const days = p === "24h" ? 1 : p === "7d" ? 7 : 30;
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { start, end: now };
}

export function fmtRange(s: string, e: string): string {
  const a = new Date(s),
    b = new Date(e);
  return `${fmtDateTime(a)} — ${fmtDateTime(b)}`;
}

export function fmtDuration(s: string, e: string): string {
  const ms = new Date(e).getTime() - new Date(s).getTime();
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.round(ms / 60_000)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}

export function fmtSeconds(n: number): string {
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const rs = Math.round(n % 60);
  return rs ? `${m}m ${rs}s` : `${m}m`;
}

// NZ standard date format: dd/mm/yyyy, rendered in Pacific/Auckland regardless
// of the viewer's machine timezone (CLAUDE.md: convert to NZ-local at the
// presentation layer). Intl formatters are constructed once at module scope.
const _NZ_DATE_FMT = new Intl.DateTimeFormat("en-NZ", {
  timeZone: "Pacific/Auckland",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const _NZ_TIME_FMT = new Intl.DateTimeFormat("en-NZ", {
  timeZone: "Pacific/Auckland",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function fmtDateNZ(d: Date): string {
  // en-NZ yields dd/mm/yyyy.
  return _NZ_DATE_FMT.format(d);
}

export function fmtTimeNZ(d: Date): string {
  // 24-hour HH:mm in NZ time. hour12:false can render "24:00" on some
  // runtimes for midnight; normalise it to "00:00".
  return _NZ_TIME_FMT.format(d).replace(/^24:/, "00:");
}

export function fmtDateTime(s: string | Date): string {
  const d = typeof s === "string" ? new Date(s) : s;
  return `${fmtDateNZ(d)} ${fmtTimeNZ(d)}`;
}

export function fmtDurationMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

export function fmtMinutesOneDp(ms: number | null): string {
  if (ms == null) return "—";
  return `${(ms / 60000).toFixed(1)} min`;
}

export function relativeTime(isoOrNull: string | null): string {
  if (!isoOrNull) return "—";
  const t = new Date(isoOrNull).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.round(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  // Derive months and years from days directly so the year threshold doesn't
  // drift early — a hardcoded 30-day month rolls over to "1 year" at ~360 days.
  const mo = Math.round(d / 30.44);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const y = Math.round(d / 365.25);
  return `${y} year${y === 1 ? "" : "s"} ago`;
}

// Task #155 — deterministic dot-colour for an assessment id, so the
// same assessment always shows the same coloured ● in lists and
// drill-throughs. 8 distinct entries (4 base palette colours + their
// /60 variants) because only `--chart-1..5` exist in index.css; we
// reuse the theme tokens (primary/secondary/accent/destructive) to
// avoid introducing chart-6/7/8.
const _DOT_PALETTE = [
  "bg-primary",
  "bg-secondary",
  "bg-accent",
  "bg-destructive",
  "bg-primary/60",
  "bg-secondary/60",
  "bg-accent/60",
  "bg-destructive/60",
];

export function assessmentDotClass(assessmentId: string): string {
  let h = 0;
  for (let i = 0; i < assessmentId.length; i++) {
    h = (h * 31 + assessmentId.charCodeAt(i)) >>> 0;
  }
  return _DOT_PALETTE[h % _DOT_PALETTE.length] ?? "";
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
